import {
  getCatalogSourceLabel,
  getLocalizedFoodName,
  isCatalogFood,
} from "../../foods/foodCatalog";
import {
  hasEquivalentUsablePickerItems,
} from "../../equivalents/equivalentLogic";
import {
  hasRecipeUsablePickerItems,
} from "../../recipes/recipeLogic";
import { getNutritionEntityDisplayName } from "../nutritionEntityNameUtils";

export const PICKER_FILTER_ALL = "all";
export const PICKER_FILTER_CREATED_BY_ME = "createdByMe";
export const PICKER_FILTER_CATALOG = "catalog";
export const PICKER_FILTER_EQUIVALENTS = "equivalents";
export const PICKER_FILTER_RECIPES = "recipes";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function getFiniteNumber(value, fallback = 0) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function buildPreviewText(names, t, maxVisible = 3) {
  const previewNames = (Array.isArray(names) ? names : [])
    .map((name) => String(name ?? "").trim())
    .filter(Boolean);

  if (previewNames.length === 0) {
    return "";
  }

  const visibleNames = previewNames.slice(0, maxVisible);
  const remainingCount = Math.max(previewNames.length - visibleNames.length, 0);

  if (remainingCount === 0) {
    return visibleNames.join("  •  ");
  }

  const foodsLabel = remainingCount === 1 ? t("food-string") : t("foods-string");
  return `${visibleNames.join("  •  ")}  •  +${remainingCount} ${String(
    foodsLabel ?? "",
  ).toLowerCase()}`;
}

function getTrimmedLabel(value) {
  return String(value ?? "").trim();
}

function getUppercaseLabel(value) {
  const trimmedValue = getTrimmedLabel(value);
  return trimmedValue ? trimmedValue.toUpperCase() : "";
}

function getFoodSourceLabel(food, t) {
  if (isCatalogFood(food)) {
    return getCatalogSourceLabel(food, t);
  }

  return typeof t === "function" ? t("createdByMe-string") : "Created by me";
}

function getFoodSearchText(food, language, sourceLabel) {
  return [
    getLocalizedFoodName(food, language),
    food?.name,
    food?.name_es,
    food?.name_en,
    food?.brand,
    food?.store,
    food?.per,
    food?.notes,
    sourceLabel,
    food?.source,
  ]
    .filter(hasText)
    .join(" ")
    .toLowerCase();
}

function getEquivalentPreviewFoods(equivalent) {
  if (Array.isArray(equivalent?.foods) && equivalent.foods.length > 0) {
    return equivalent.foods;
  }

  if (Array.isArray(equivalent?.options)) {
    return equivalent.options
      .map((option) => String(option?.name ?? "").trim())
      .filter(Boolean);
  }

  return [];
}

function getRecipePreviewFoods(recipe) {
  if (Array.isArray(recipe?.foods) && recipe.foods.length > 0) {
    return recipe.foods;
  }

  if (Array.isArray(recipe?.ingredients)) {
    return recipe.ingredients
      .map((ingredient) => String(ingredient?.name ?? "").trim())
      .filter(Boolean);
  }

  return [];
}

export function normalizePickerFoodItem(food, { language = "en", t } = {}) {
  if (!food || typeof food !== "object") {
    return null;
  }

  const title = getLocalizedFoodName(food, language).trim();
  const subtitle = getFoodSourceLabel(food, t);

  return {
    id: String(food?.id ?? food?.code ?? title).trim(),
    key: `food:${String(food?.id ?? food?.code ?? title).trim()}`,
    entityKind: "food",
    title,
    subtitle,
    previewText: "",
    nutrients: {
      calories: getFiniteNumber(food?.calories),
      carbs: getFiniteNumber(food?.carbs),
      protein: getFiniteNumber(food?.protein),
      fats: getFiniteNumber(food?.fats),
      fiber: getFiniteNumber(food?.fiber),
    },
    sourceKind: isCatalogFood(food) ? "catalog" : "coach",
    searchText: getFoodSearchText(food, language, subtitle),
    sortName: title.toLowerCase(),
    sourceRecord: food,
  };
}

export function normalizePickerEquivalentItem(
  equivalent,
  { language = "en", t } = {},
) {
  if (
    !equivalent ||
    typeof equivalent !== "object" ||
    !hasEquivalentUsablePickerItems(equivalent)
  ) {
    return null;
  }

  const title = getNutritionEntityDisplayName("equivalent", equivalent, language);
  const previewFoods = getEquivalentPreviewFoods(equivalent);
  const previewText = buildPreviewText(previewFoods, t);
  const equivalentLabel =
    typeof t === "function"
      ? t("equivalentResultType-string", "Equivalent")
      : "Equivalent";

  return {
    id: String(equivalent?.id ?? title).trim(),
    key: `equivalent:${String(equivalent?.id ?? title).trim()}`,
    entityKind: "equivalent",
    title,
    subtitle: "",
    previewText,
    nutrients: {
      calories: getFiniteNumber(equivalent?.calories),
      carbs: getFiniteNumber(equivalent?.carbs),
      protein: getFiniteNumber(equivalent?.protein),
      fats: getFiniteNumber(equivalent?.fats),
      fiber: getFiniteNumber(equivalent?.fiber),
    },
    sourceKind: "coach",
    searchText: [
      title,
      equivalentLabel,
      previewText,
      ...previewFoods,
      equivalent?.searchableText,
    ]
      .filter(hasText)
      .join(" ")
      .toLowerCase(),
    sortName: title.toLowerCase(),
    sourceRecord: equivalent,
  };
}

export function normalizePickerRecipeItem(recipe, { language = "en", t } = {}) {
  if (!recipe || typeof recipe !== "object" || !hasRecipeUsablePickerItems(recipe)) {
    return null;
  }

  const title = getNutritionEntityDisplayName("recipe", recipe, language);
  const previewFoods = getRecipePreviewFoods(recipe);
  const previewText = buildPreviewText(previewFoods, t);
  const subtitle =
    typeof t === "function" && recipe?.typeKey
      ? getUppercaseLabel(t(recipe.typeKey))
      : typeof t === "function"
        ? t("recipeResultType-string", "Recipe")
        : "Recipe";

  return {
    id: String(recipe?.id ?? title).trim(),
    key: `recipe:${String(recipe?.id ?? title).trim()}`,
    entityKind: "recipe",
    title,
    subtitle,
    previewText,
    nutrients: {
      calories: getFiniteNumber(recipe?.calories),
      carbs: getFiniteNumber(recipe?.carbs),
      protein: getFiniteNumber(recipe?.protein),
      fats: getFiniteNumber(recipe?.fats),
      fiber: getFiniteNumber(recipe?.fiber),
    },
    sourceKind: "coach",
    searchText: [
      title,
      subtitle,
      previewText,
      recipe?.description,
      ...previewFoods,
      recipe?.searchableText,
    ]
      .filter(hasText)
      .join(" ")
      .toLowerCase(),
    sortName: title.toLowerCase(),
    sourceRecord: recipe,
  };
}

export function buildPickerBrowserItems(
  { foods = [], equivalents = [], recipes = [] } = {},
  options = {},
) {
  return [
    ...(Array.isArray(foods) ? foods : []).map((food) =>
      normalizePickerFoodItem(food, options),
    ),
    ...(Array.isArray(equivalents) ? equivalents : []).map((equivalent) =>
      normalizePickerEquivalentItem(equivalent, options),
    ),
    ...(Array.isArray(recipes) ? recipes : []).map((recipe) =>
      normalizePickerRecipeItem(recipe, options),
    ),
  ].filter(Boolean);
}

export function buildCanonicalPickerResults(
  {
    foods = [],
    equivalents = [],
    recipes = [],
  } = {},
  {
    language = "en",
    t,
    searchText = "",
    filterKey = PICKER_FILTER_ALL,
    sortField = null,
    limit = 8,
  } = {},
) {
  const foodItems = buildPickerBrowserItems(
    {
      foods,
    },
    {
      language,
      t,
    },
  ).filter((item) => matchesPickerBrowserFilter(item, filterKey));
  const nonFoodItems = buildPickerBrowserItems(
    {
      equivalents,
      recipes,
    },
    {
      language,
      t,
    },
  ).filter(
    (item) =>
      matchesPickerBrowserSearch(item, searchText) &&
      matchesPickerBrowserFilter(item, filterKey),
  );
  const resolvedLimit = Math.max(1, Number(limit) || 8);
  const hasActiveSearch = String(searchText ?? "").trim().length > 0;

  if (filterKey === PICKER_FILTER_EQUIVALENTS || filterKey === PICKER_FILTER_RECIPES) {
    return sortPickerBrowserItems(nonFoodItems, sortField).slice(0, resolvedLimit);
  }

  if (!hasActiveSearch) {
    return sortPickerBrowserItems(
      [...foodItems, ...nonFoodItems],
      sortField,
    ).slice(0, resolvedLimit);
  }

  return [
    ...foodItems,
    ...sortPickerBrowserItems(nonFoodItems, sortField),
  ].slice(0, resolvedLimit);
}

export function matchesPickerBrowserSearch(item, searchText) {
  const normalizedSearch = String(searchText ?? "")
    .trim()
    .toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  return String(item?.searchText ?? "").includes(normalizedSearch);
}

export function matchesPickerBrowserFilter(item, filterKey) {
  if (filterKey === PICKER_FILTER_ALL) {
    return true;
  }

  if (filterKey === PICKER_FILTER_CREATED_BY_ME) {
    if (item?.entityKind === "food") {
      return item?.sourceKind === "coach";
    }

    return item?.entityKind === "equivalent" || item?.entityKind === "recipe";
  }

  if (filterKey === PICKER_FILTER_CATALOG) {
    return item?.entityKind === "food" && item?.sourceKind === "catalog";
  }

  if (filterKey === PICKER_FILTER_EQUIVALENTS) {
    return item?.entityKind === "equivalent";
  }

  if (filterKey === PICKER_FILTER_RECIPES) {
    return item?.entityKind === "recipe";
  }

  return true;
}

export function sortPickerBrowserItems(items, sortField = null) {
  const list = Array.isArray(items) ? items : [];

  if (!sortField) {
    return [...list].sort((leftItem, rightItem) =>
      String(leftItem?.sortName ?? "").localeCompare(
        String(rightItem?.sortName ?? ""),
        undefined,
        { sensitivity: "base" },
      ),
    );
  }

  return [...list].sort((leftItem, rightItem) => {
    const leftValue = getFiniteNumber(leftItem?.nutrients?.[sortField]);
    const rightValue = getFiniteNumber(rightItem?.nutrients?.[sortField]);

    if (rightValue !== leftValue) {
      return rightValue - leftValue;
    }

    return String(leftItem?.sortName ?? "").localeCompare(
      String(rightItem?.sortName ?? ""),
      undefined,
      { sensitivity: "base" },
    );
  });
}

export function getPickerBrowserItemKey(item) {
  return String(item?.key ?? item?.id ?? "").trim();
}
